import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Minimize2, X, Plane, Lightbulb, Lock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import vouchersData from '../../../../../../data/vouchers/vouchersData.json';
import { getAvailableImages } from '../../../../../../assets/images/Loading_img';

const { steps = [], stepTemplates: STEP_TEMPLATES = [], tips = [] } = vouchersData.aiVoucherSteps || {};

const AiAnalysisProgress = ({ analysisState, setAnalysisMinimized, resetGlobalAnalysis, hideMinimize }) => {
  const navigate = useNavigate();

  const progress = Math.round(analysisState.progress || 0);
  const [timerStep, setTimerStep] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);

  // Dynamic image list (supports up to 10+ images)
  const images = useMemo(() => getAvailableImages(), []);

  // Build loading steps mapped to available images
  const loadingSteps = useMemo(() => {
    return images.map((img, i) => {
      const template = STEP_TEMPLATES[i % STEP_TEMPLATES.length];
      return {
        ...template,
        image: img
      };
    });
  }, [images]);

  const totalSteps = loadingSteps.length || 1;

  // Infinite looping carousel step timer (cycles 0 -> 1 -> ... -> total-1 -> 0)
  useEffect(() => {
    let interval;
    if (analysisState.isAnalyzing) {
      setTimerStep(0);
      interval = setInterval(() => {
        setTimerStep(prev => (prev + 1) % totalSteps);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [analysisState.isAnalyzing, totalSteps]);

  // Rotate tips every 4 seconds
  useEffect(() => {
    let interval;
    if (analysisState.isAnalyzing) {
      interval = setInterval(() => {
        setTipIndex(prev => (prev + 1) % tips.length);
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [analysisState.isAnalyzing]);

  // Current active step index in infinite loop
  const currentStepIndex = timerStep % totalSteps;
  const currentStep = loadingSteps[currentStepIndex] || loadingSteps[0];
  const activeStepperDotIndex = currentStep.stepperIndex;

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) { // ESC key
        setAnalysisMinimized(true);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [setAnalysisMinimized]);

  const modalContent = (
    <>
      {analysisState.isAnalyzing && !analysisState.isMinimized && (
        <div className="fixed inset-0 z-[999999] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto animate-in fade-in duration-300">
          <div className="relative w-full max-w-2xl bg-white rounded-2xl sm:rounded-[28px] md:rounded-[32px] p-4 sm:p-6 md:p-7 shadow-2xl border border-slate-100 overflow-y-auto overflow-x-hidden flex flex-col justify-between my-auto max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] animate-in zoom-in-95 duration-300">
            
            {/* Top Bar Header */}
            {!hideMinimize && (
              <div className="w-full flex items-center justify-end mb-1 sm:mb-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAnalysisMinimized(true);
                  }}
                  className="p-1.5 sm:p-2 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-700 rounded-full transition-all border border-slate-200 cursor-pointer shadow-2xs"
                  title="Minimize (ESC)"
                >
                  <Minimize2 size={15} />
                </button>
              </div>
            )}

            {/* Main Content Grid: Seamless Overlapping Crossfade for Text & Image */}
            <div className="grid grid-cols-12 gap-3 sm:gap-4 md:gap-6 items-center my-1 sm:my-2 min-h-[100px] sm:min-h-[120px] md:min-h-[145px]">
              <div className="col-span-7 relative min-h-[80px] sm:min-h-[95px] grid grid-cols-1 grid-rows-1 items-center text-left">
                <AnimatePresence initial={false}>
                  <motion.div
                    key={`title-${currentStepIndex}`}
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -3 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="col-start-1 row-start-1 w-full flex flex-col justify-center items-start text-left space-y-1 sm:space-y-1.5"
                  >
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-medium text-slate-800 tracking-tight leading-tight text-left">
                      {currentStep.prefix}
                      <span className="font-extrabold text-[#067978]">
                        {currentStep.highlight}
                      </span>
                    </h2>
                    <p className="text-slate-500 text-xs sm:text-[13px] font-normal leading-relaxed max-w-sm text-left mx-0">
                      {currentStep.subtitle}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Right Side Illustration Image (Responsive scaling) */}
              <div className="col-span-5 h-28 sm:h-36 md:h-40 w-full relative grid grid-cols-1 grid-rows-1 items-center justify-center overflow-hidden">
                <AnimatePresence initial={false}>
                  <motion.img
                    key={`loading-img-${currentStepIndex}`}
                    src={currentStep.image}
                    alt="Loading Illustration"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.02 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="col-start-1 row-start-1 w-full h-full max-h-28 sm:max-h-36 md:max-h-40 object-contain select-none pointer-events-none drop-shadow-sm mx-auto"
                  />
                </AnimatePresence>
              </div>
            </div>

            {/* Scroll Progress Bar with Gliding Flight Spearhead */}
            <div className="w-full space-y-2.5 sm:space-y-3 my-2 sm:my-2.5">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="relative flex-1 h-[6px] bg-slate-100 rounded-full overflow-visible">
                  <motion.div
                    className="h-full bg-[#067978] relative overflow-visible rounded-full shadow-[0_0_10px_rgba(6,121,120,0.4)]"
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  >
                    {/* The Gliding Plane Circle Node */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-30 flex items-center justify-center bg-white border-2 border-[#067978] rounded-full w-5 h-5 sm:w-6 sm:h-6 shadow-[0_2px_8px_rgba(6,121,120,0.4)] hover:scale-110 transition-all duration-200">
                      <Plane size={11} className="text-[#067978] rotate-90" />
                    </div>
                  </motion.div>
                </div>

                {/* Percentage text */}
                <span className="text-xs font-semibold text-[#067978] shrink-0 min-w-[32px] text-right">
                  {progress}%
                </span>
              </div>

              {/* Stepper Timeline Dots */}
              <div className="relative grid grid-cols-4 gap-1 sm:gap-2 pt-0.5">
                <div className="absolute top-2.5 left-6 right-6 sm:left-8 sm:right-8 h-0.5 bg-slate-200 -z-0" />
                {steps.map((step, idx) => {
                  const isDoneOrActive = idx <= activeStepperDotIndex;
                  return (
                    <div key={idx} className="flex flex-col items-center text-center relative z-10">
                      <div
                        className={`w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full transition-all duration-300 ${
                          isDoneOrActive
                            ? "bg-[#067978] ring-3 sm:ring-4 ring-teal-100"
                            : "bg-slate-300"
                        }`}
                      />
                      <span
                        className={`text-[9px] sm:text-[10px] md:text-[11px] mt-1 sm:mt-1.5 leading-tight px-0.5 line-clamp-2 sm:line-clamp-none ${
                          isDoneOrActive
                            ? "font-medium text-[#067978]"
                            : "font-normal text-slate-400"
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Light Teal Multi-Tip Box with Smooth Crossfade */}
            <div className="bg-[#EAF6F4] border border-teal-100/80 rounded-xl sm:rounded-2xl p-2.5 sm:p-3.5 flex items-start sm:items-center gap-2.5 sm:gap-3.5 my-1.5 sm:my-2 shadow-2xs">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white text-[#067978] flex items-center justify-center shrink-0 shadow-2xs mt-0.5 sm:mt-0">
                <Lightbulb size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[11px] sm:text-xs font-bold text-[#067978] block leading-tight mb-0.5">Did you know?</span>
                <AnimatePresence mode="wait">
                  <motion.p
                    key={`tip-${tipIndex}`}
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -3 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="text-[11px] sm:text-xs font-normal text-slate-600 leading-relaxed m-0 line-clamp-2 sm:line-clamp-none"
                  >
                    {tips[tipIndex]}
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>

            {/* Cancel Action & Sub-footer */}
            <div className="mt-2 sm:mt-3 flex flex-col items-center text-center space-y-1">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  resetGlobalAnalysis();
                }}
                className="px-5 sm:px-6 py-1.5 sm:py-2 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-full font-normal text-[11px] sm:text-xs transition-all border border-rose-100 cursor-pointer shadow-2xs"
              >
                Cancel Voucher Generation
              </button>
              <p className="text-[10px] sm:text-[11px] text-slate-400 font-normal">
                You can safely close this modal, we'll notify you once it's ready.
              </p>
            </div>

            {/* Bottom Tagline */}
            <div className="mt-2.5 sm:mt-3.5 pt-2 sm:pt-2.5 border-t border-slate-100 text-[9px] sm:text-[10px] font-bold text-slate-300 uppercase tracking-widest text-center">
              SAME JOURNEYS, GREATER IMPACT
            </div>
          </div>
        </div>
      )}

      {/* Error Alert Modal */}
      {analysisState.error && !analysisState.isAnalyzing && (
        <div className="fixed inset-0 z-[999999] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto animate-in fade-in duration-300">
          <div className="relative w-full max-w-lg bg-white rounded-2xl sm:rounded-[28px] md:rounded-[32px] p-5 sm:p-8 shadow-2xl border border-slate-100 flex flex-col items-center text-center my-auto max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto animate-in zoom-in-95 duration-300">
            {analysisState.error?.toLowerCase().includes("rate limit") || analysisState.error?.toLowerCase().includes("30 minutes") ? (
              <>
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mb-4 border border-amber-200/60">
                  <Lock size={28} className="stroke-[2.2]" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 tracking-tight">Rate Limit Reached</h2>
                <p className="text-slate-500 text-xs sm:text-sm mb-6 max-w-xs leading-relaxed font-normal">
                  You can create a maximum of 10 vouchers every 30 minutes. Please wait a few minutes before generating more vouchers.
                </p>
                <div className="flex flex-col gap-2.5 w-full max-w-xs">
                  <button
                    onClick={resetGlobalAnalysis}
                    className="w-full py-3 bg-[#067978] text-white rounded-full font-bold text-xs sm:text-sm hover:bg-[#056362] transition-all shadow-md cursor-pointer"
                  >
                    Understood
                  </button>
                </div>
              </>
            ) : analysisState.error?.toLowerCase().includes("subscription") ? (
              <>
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mb-4 border border-amber-200/60">
                  <Lock size={28} className="stroke-[2.2]" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 tracking-tight">Subscription Required</h2>
                <p className="text-slate-500 text-xs sm:text-sm mb-6 max-w-xs leading-relaxed font-normal">
                  Active subscription or trial required to create vouchers. Please upgrade your plan.
                </p>
                <div className="flex flex-col gap-2.5 w-full max-w-xs">
                  <button
                    onClick={() => {
                      resetGlobalAnalysis();
                      navigate('/subscription');
                    }}
                    className="w-full py-3 bg-[#067978] text-white rounded-full font-bold text-xs sm:text-sm hover:bg-[#056362] transition-all shadow-md cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span>Upgrade Plan for More</span>
                    <ArrowRight size={16} />
                  </button>
                  <button
                    onClick={resetGlobalAnalysis}
                    className="w-full py-2 bg-slate-100 text-slate-600 rounded-full font-bold text-xs hover:bg-slate-200 transition-all cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center mb-4 border border-rose-100">
                  <X size={28} />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 tracking-tight">Extraction Failed</h2>
                <p className="text-slate-500 text-xs sm:text-sm mb-6 max-w-xs leading-relaxed font-normal">
                  {analysisState.error || "Unable to read these documents clearly."}
                </p>
                <button
                  onClick={resetGlobalAnalysis}
                  className="w-full max-w-xs py-3 bg-[#067978] text-white rounded-full font-bold text-xs sm:text-sm hover:bg-[#056362] transition-all shadow-md cursor-pointer"
                >
                  Try Different Files
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modalContent, document.body);
};

export default AiAnalysisProgress;
